import type { PaginationDto, PaginationResponseDto } from "core/dto";
import { ORDER_BY } from "core/interfaces";
import type { PageMetaDto } from "shared/dto";

import { getCustomerDefault } from "~/common/defaults/customer.default";
import { getPaginationDefault } from "~/common/defaults/pagination.default";
import { getPaginationResponseDefault } from "~/common/defaults/pagination-response.default";
import type { UpdateCustomerByIdDto } from "~/common/interfaces/dto/customers/update-customer-by-id.dto";
import type { Customer } from "~/common/interfaces/entities/customer.interface";
import type { Sort } from "~/common/interfaces/sort.interface";

export const useCustomersStore = defineStore(
	"customers",
	() => {
		const currentCustomer = ref<Customer>(getCustomerDefault());
		const customers = ref<Customer[]>([]);
		const meta = ref<PageMetaDto>(getPaginationResponseDefault());
		const recentGetAllQuery = ref<PaginationDto>();
		const tableSort = ref<Sort>({
			columns: ["firstName", "lastName", "email", "phoneNumber", "orders"],
			orderBy: ["email", ORDER_BY.ASC]
		});

		const getAll = async (
			paginationDto?: Partial<PaginationDto>
		): Promise<PaginationResponseDto<Customer>> => {
			const [column, orderBy] = tableSort.value.orderBy;

			recentGetAllQuery.value = {
				...getPaginationDefault(),
				...paginationDto,
				column,
				orderBy
			};

			const res = await useNuxtApp().$apiFetch<PaginationResponseDto<Customer>>(
				"/customers",
				{
					query: recentGetAllQuery.value
				}
			);
			customers.value = res.data;
			meta.value = res.meta;

			return res;
		};
		const getById = (id: number): Promise<Customer> => {
			return useNuxtApp().$apiFetch<Customer>(`/customers/${id}`);
		};
		const patchById = (
			id: number,
			dto: UpdateCustomerByIdDto
		): Promise<Customer> => {
			return useNuxtApp().$apiFetch<Customer>(`/customers/${id}`, {
				method: "PATCH",
				body: dto
			});
		};

		return {
			currentCustomer,
			customers,
			meta,
			recentGetAllQuery,
			tableSort,

			getAll,
			getById,
			patchById
		};
	},
	{
		persist: {
			paths: ["tableSort.orderBy"]
		}
	}
);
